import { Component, Element, Event, EventEmitter, Fragment, h, Host, Prop, Watch } from "@stencil/core";

import {
  StzhBreadcrumbItem,
  StzhCardSuperteaserClickEvent,
  StzhDatalistItemEntry,
  StzhLocaleAdapter,
} from "../../../index";

import { hasSlot } from "../../../utils/utils";

import { StzhCardSuperteaserLocalizedText } from "./stzh-card-superteaser.localization";

import PlaceholderImage from "./assets/placeholder-image.svg";

/**
 * @slot heading - Heading text (alternative for heading property)
 * @slot description - Description text (alternative for description property)
 * @slot image - Image
 */
@Component({
  tag: "stzh-card-superteaser",
  styleUrl: "stzh-card-superteaser.scss",
  scoped: true,
})
export class StzhCardSuperteaser {
  /** Translation strings */
  @Prop() localization: StzhCardSuperteaserLocalizedText;

  /** Href of card */
  @Prop({ reflect: true }) href: string;

  /** target if the button is used as link (if href used)  */
  @Prop() target: string;

  /** Download attribute (if href is used) */
  @Prop() download: string;

  /**
   * For Adobe Analytics.
   * Will be rendered as value of `s-object-id` attribute to the link elements.
   * Default value will be taken from `heading` prop/slot.
   */
  @Prop() analyticsId: string;

  /** Heading (alternative for heading slot) */
  @Prop() heading: string = "";

  /** Heading level */
  @Prop() headingLevel: "1" | "2" | "3" | "4" = "3";

  /** Description text (alternative for description slot) */
  @Prop() description: string = "";

  /** Tag */
  @Prop() tag: string;

  /** Topic */
  @Prop() topic: string;

  /** Service */
  @Prop() service: string;

  /** Dateline */
  @Prop() dateline: string;

  /** Meta */
  @Prop() meta: string[] | string;
  private _meta: string[];

  /** Breadcrumb items */
  @Prop() breadcrumbItems: StzhBreadcrumbItem[] | string;

  /** Datalist items */
  @Prop() datalistItems: StzhDatalistItemEntry[] | string = [];

  /** Event start date */
  @Prop() eventDateStart: Date | string;

  /** Event start date */
  @Prop() eventDateEnd: Date | string;

  /** Event location */
  @Prop() eventLocation: string;

  /** Enable placeholder image if image slot not used */
  @Prop() placeholderImageEnabled: boolean = false;

  /** Enable if superteaser is on homepage. This will change some stylings. */
  @Prop() isOnHomepage: boolean = false;

  /** Image position */
  @Prop({ reflect: true }) imagePosition: "default" | "right" = "default";

  /** Download heading */
  @Prop() downloadHeading: string;

  /** Download meta */
  @Prop() downloadMeta: string[] | string;
  private _downloadMeta: string[];

  /**
   * Date adapter, for custom parsing/formatting.
   * Must be object with a `parse` function which accepts a `string` and returns a `Date`,
   * and a `format` function which accepts a `Date` and returns a `string`.
   */
  @Prop() dateAdapter: StzhLocaleAdapter;

  /** Automatic Hyphens */
  @Prop({ reflect: true }) hyphensEnabled: boolean = false;

  @Element() element: HTMLStzhListElement;

  /** Click event (if href is used) */
  @Event() stzhClick: EventEmitter<StzhCardSuperteaserClickEvent>;

  @Watch("meta")
  metaWatcher(newValue: string | string[]) {
    if (!newValue) {
      this._meta = [];
    } else if (typeof newValue === "string") {
      try {
        this._meta = JSON.parse(newValue);
      } catch (e) {
        this._meta = [];
      }
    } else {
      this._meta = newValue;
    }
  }

  @Watch("downloadMeta")
  downloadMetaWatcher(newValue: string | string[]) {
    if (!newValue) {
      this._downloadMeta = [];
    } else if (typeof newValue === "string") {
      try {
        this._downloadMeta = JSON.parse(newValue);
      } catch (e) {
        this._downloadMeta = [];
      }
    } else {
      this._downloadMeta = newValue;
    }
  }

  private headingLinkElement: HTMLAnchorElement | HTMLDivElement;
  private headingTextElement: HTMLDivElement;

  private handleCardClick = (originalEvent: MouseEvent) => {
    this.stzhClick.emit({
      component: "stzh-card-superteaser",
      originalEvent,
      href: this.href,
    });
  };

  private hasProperStartAndOrEndDate(startDate: Date | undefined, endDate: Date | undefined) {
    return !(!!startDate && startDate.getHours() === 0 && !endDate);
  }

  componentDidRender() {
    requestAnimationFrame(() => {
      const objectId = this.analyticsId || this.headingTextElement?.innerText;

      if (objectId) {
        this.headingLinkElement?.setAttribute("s-object-id", objectId);
      }
    });
  }

  async componentWillLoad() {
    this.metaWatcher(this.meta);
    this.downloadMetaWatcher(this.downloadMeta);

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "card-superteaser");
    }

    if (!this.dateAdapter) {
      this.dateAdapter = window.stzhComponents.utils.createFormatParseAdapter(
        this.localization.$formats,
        this.localization.$globals,
      );
    }
  }

  render() {
    const hasHeading = !!this.heading || hasSlot(this.element, "heading");
    const hasDescription = !!this.description || hasSlot(this.element, "description");
    const hasImageSlot = hasSlot(this.element, "image");

    const eventDateStart = this.eventDateStart && new Date(this.eventDateStart);
    const eventDateEnd = this.eventDateEnd && new Date(this.eventDateEnd);
    if (eventDateStart && !hasImageSlot) {
      this.placeholderImageEnabled = true;
    }

    const hasImage = hasImageSlot || this.placeholderImageEnabled;

    const classes = {
      "stzh-card-superteaser": true,
      "stzh-card-superteaser--has-heading": hasHeading,
      "stzh-card-superteaser--has-description": hasDescription,
      "stzh-card-superteaser--has-description-wrapper": hasDescription || !!eventDateStart || !!this.eventLocation,
      "stzh-card-superteaser--has-link": !!this.href,
      "stzh-card-superteaser--has-service": !!this.service,
      "stzh-card-superteaser--has-tag": !!this.tag,
      "stzh-card-superteaser--has-topic": !!this.topic,
      "stzh-card-superteaser--has-image": hasImage,
      "stzh-card-superteaser--has-image-default": hasImage && this.imagePosition === "default",
      "stzh-card-superteaser--has-image-right": hasImage && this.imagePosition === "right",
      "stzh-card-superteaser--has-meta": this._meta.length > 0,
      "stzh-card-superteaser--has-download": !!this.downloadHeading || this._downloadMeta.length > 0,
      "stzh-card-superteaser--has-breadcrumb": !!this.breadcrumbItems,
      "stzh-card-superteaser--is-on-homepage": this.isOnHomepage,
      "stzh-card-superteaser--is-event": !!eventDateStart,
      "stzh-card-superteaser--hyphens": this.hyphensEnabled,
    };

    const Heading = `h${this.headingLevel}`;
    const HeadingLink = this.href ? "a" : "div";

    return (
      <Host has-image={hasImage}>
        <article class={classes} aria-label={this.heading} role="listitem">
          {this.tag && <stzh-tag class="stzh-card-superteaser__tag" label={this.tag} nonInteractive={true}></stzh-tag>}

          <div class="stzh-card-superteaser__heading-wrapper">
            {this.breadcrumbItems && (
              <stzh-breadcrumb
                class="stzh-card-superteaser__breadcrumb"
                items={this.breadcrumbItems}
                variant="teaser"
              ></stzh-breadcrumb>
            )}

            {this.dateline && <div class="stzh-card-superteaser__dateline">{this.dateline}</div>}

            {this.topic && <div class="stzh-card-superteaser__topic">{this.topic}</div>}

            <Heading
              class={`stzh-card-superteaser__heading ${hasSlot(this.element, "heading") ? "stzh-card-superteaser__heading--slotted" : ""}`}
            >
              <HeadingLink
                ref={el => (this.headingLinkElement = el as HTMLAnchorElement | HTMLDivElement)}
                class="stzh-card-superteaser__heading-link"
                href={this.href}
                target={this.target}
                download={this.download}
                onClick={this.handleCardClick}
              >
                {this.heading ? (
                  <div
                    class="stzh-card-superteaser__heading-link-text"
                    ref={el => (this.headingTextElement = el as HTMLDivElement)}
                    innerHTML={this.heading}
                  ></div>
                ) : (
                  <div
                    class="stzh-card-superteaser__heading-link-text"
                    ref={el => (this.headingTextElement = el as HTMLDivElement)}
                  >
                    <slot name="heading"></slot>
                  </div>
                )}
              </HeadingLink>
            </Heading>
          </div>

          <div class="stzh-card-superteaser__description-wrapper">
            {(eventDateStart || this.eventLocation) && (
              <div class="stzh-card-superteaser__event-infos">
                {eventDateStart && (
                  <div class="stzh-card-superteaser__event-date">
                    <span>{this.dateAdapter.format(eventDateStart, "dateLong")}</span>
                    {this.hasProperStartAndOrEndDate(eventDateStart, eventDateEnd) && (
                      <span>
                        {!this.isOnHomepage && <span>,&nbsp;</span>}
                        {this.dateAdapter.formatSpan(eventDateStart, eventDateEnd, "time")}
                      </span>
                    )}
                  </div>
                )}
                {this.eventLocation && <div class="stzh-card-superteaser__event-location">{this.eventLocation}</div>}
              </div>
            )}

            <div
              class={`stzh-card-superteaser__description ${hasSlot(this.element, "heading") ? "stzh-card-superteaser__description--slotted" : ""}`}
            >
              {this.description ? this.description : <slot name="description"></slot>}
            </div>
          </div>

          <div class="stzh-card-superteaser__image">
            {eventDateStart && (
              <div class="stzh-card-superteaser__event-date-teaser">
                <div class="stzh-card-superteaser__event-date-teaser-day-name">
                  {this.localization.$globals.dayNamesShort[eventDateStart.getDay()]}
                </div>
                <div class="stzh-card-superteaser__event-date-teaser-day">
                  {eventDateStart.getDate() < 10 && "0"}
                  {eventDateStart.getDate()}
                </div>
                <div class="stzh-card-superteaser__event-date-teaser-month">
                  {this.localization.$globals.monthNamesShort[eventDateStart.getMonth()]}
                </div>
              </div>
            )}
            {this.service && <div class="stzh-card-superteaser__service">{this.service}</div>}
            <slot name="image"></slot>
            {this.placeholderImageEnabled && !hasImageSlot && (
              <stzh-ratio ratio="2:1">
                <div class="stzh-card-superteaser__image-placeholder" innerHTML={PlaceholderImage}></div>
              </stzh-ratio>
            )}
          </div>

          {(this._meta.length > 0 || this.datalistItems || !!this.downloadHeading || this._downloadMeta.length > 0) && (
            <div class="stzh-card-superteaser__meta-wrapper">
              {this.datalistItems && (
                <stzh-datalist class="stzh-card-searchresult__datalist" items={this.datalistItems}></stzh-datalist>
              )}

              {this._meta.length > 0 && (
                <div class="stzh-card-superteaser__meta">
                  <div class="stzh-card-superteaser__meta-list">
                    {this._meta.map(meta => (
                      <Fragment>
                        <div class="stzh-card-superteaser__meta-list-item">{meta}</div>
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}

              {(!!this.downloadHeading || this._downloadMeta.length > 0) && (
                <div class="stzh-card-superteaser__download">
                  <div class="stzh-card-superteaser__download-data">
                    {this.downloadHeading && (
                      <div class="stzh-card-superteaser__download-heading">{this.downloadHeading}</div>
                    )}
                    {this._downloadMeta.length > 0 && (
                      <div class="stzh-card-superteaser__download-meta-list">
                        {this._downloadMeta.map(meta => (
                          <Fragment>
                            <div class="stzh-card-superteaser__download-meta-list-item">{meta}</div>
                          </Fragment>
                        ))}
                      </div>
                    )}
                  </div>

                  <div class="stzh-card-superteaser__download-button">
                    <stzh-icon class="stzh-card-superteaser__download-icon" name="download"></stzh-icon>
                  </div>
                </div>
              )}
            </div>
          )}
        </article>
      </Host>
    );
  }
}
